import ArrowLeft from '@assets/svg/ArrowLeft';
import ArrowRight from '@assets/svg/ArrowRight';
import { Button, Spinner, cn } from 'shared-components';
import { AdminPortalKeys, Namespace } from '@i18n/translations';
import { type FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type PaginationPage, type PaginationProps } from './Pagination.types';

const buildPagesToRender = (pageCount: number, currentPage: number, maxPagesToShow: number): PaginationPage[] => {
  const pages: PaginationPage[] = [];
  let renderPlaceholder = true;

  for (let i = 1; i <= pageCount; i++) {
    const isEdge = i === 1 || i === pageCount || pageCount <= maxPagesToShow;
    const isNearCurrent = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2].includes(i);

    if (isEdge || isNearCurrent) {
      pages.push({ page: i, renderPlaceholder: false, showPage: true });
      renderPlaceholder = true;
    } else {
      pages.push({ page: i, renderPlaceholder, showPage: false });
      renderPlaceholder = false;
    }
  }

  return pages;
};

const Pagination: FunctionComponent<PaginationProps> = ({
  pageCount,
  currentPage,
  onPageChange,
  position = 'bottom',
  maxPagesToShow = 10,
  loading = false,
  dataTestid,
  className,
}) => {
  const { t } = useTranslation(Namespace.AdminPortal);
  const pagesToRender = useMemo(
    () => buildPagesToRender(pageCount, currentPage, maxPagesToShow),
    [pageCount, currentPage, maxPagesToShow],
  );

  const Root = position === 'top' ? 'nav' : 'div';

  return (
    <Root
      className={cn(
        'flex items-center justify-center gap-4',
        position === 'bottom' && 'border-t border-black pt-4',
        className,
      )}
      data-testid={dataTestid}
      role={position === 'top' ? undefined : 'group'}
      aria-label={t(AdminPortalKeys.Pagination_Label)}
    >
      {currentPage !== 1 && (
        <Button
          name="pagination-previous"
          variant="outline"
          size="icon"
          aria-label={t(AdminPortalKeys.Pagination_PreviousPage)}
          disabled={currentPage === 1 || loading}
          onClick={() => onPageChange(currentPage - 1)}
          startIcon={
            <ArrowLeft
              className={cn('**:stroke-secondary-900 **:stroke-3! hover:**:stroke-primary-800', {
                '**:stroke-secondary-400!': currentPage === 1 || loading,
              })}
            />
          }
          className={cn('size-7.5! rounded-full! border-secondary-900 bg-transparent! text-secondary-900', {
            'border-secondary-400!': currentPage === 1 || loading,
          })}
        />
      )}

      <ul className="m-0 flex list-none items-center gap-4 p-0">
        {pagesToRender.map((page) =>
          page.showPage ? (
            <li key={page.page}>
              <Button
                name={`pagination-page-${page.page}`}
                variant="ghost"
                disabled={loading}
                onClick={() => onPageChange(page.page)}
                aria-current={page.page === currentPage ? 'page' : undefined}
                className={cn('bg-transparent! text-body-2 font-normal!', {
                  'text-body-1 font-bold! text-primary-600': page.page === currentPage,
                  'text-secondary-400!': loading,
                })}
              >
                {page.page}
              </Button>
            </li>
          ) : page.renderPlaceholder ? (
            <li key={page.page} aria-hidden="true" className={cn('text-body-2', { 'text-secondary-400!': loading })}>
              &hellip;
            </li>
          ) : null,
        )}
      </ul>

      {loading && (
        <Spinner size="sm" dataTestid="pagination-spinner" aria-label={t(AdminPortalKeys.Pagination_Loading)} />
      )}

      {currentPage !== pageCount && (
        <Button
          name="pagination-next"
          variant="outline"
          size="icon"
          aria-label={t(AdminPortalKeys.Pagination_NextPage)}
          disabled={currentPage === pageCount || loading}
          onClick={() => onPageChange(currentPage + 1)}
          startIcon={
            <ArrowRight
              className={cn('**:stroke-secondary-900 **:stroke-3! hover:**:stroke-primary-800', {
                '**:stroke-secondary-400!': currentPage === pageCount || loading,
              })}
            />
          }
          className={cn('size-7.5! rounded-full! border-secondary-900 bg-transparent! text-secondary-900', {
            'border-secondary-400!': currentPage === pageCount || loading,
          })}
        />
      )}
    </Root>
  );
};

Pagination.displayName = 'Pagination';
export { Pagination };
